import { useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CampaignPlanStep,
  CampaignPlanTier,
} from "@shared-core/data/api-types";
import {
  artworkCodesLine,
  MAX_ARTWORK_CODE_LENGTH,
  NO_TIERS_YET_NOTE,
  stepAudienceLabel,
  tierChoiceHelp,
} from "@shared-core/domain/direct-mail-campaigns";
import { Button } from "../../app/components/primitives/button";
import { Checkbox } from "../../app/components/primitives/checkbox";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";
import { failureLogFields, readCampaignActionError } from "./actionErrors";

/** Tick boxes for a set of tiers. */
export function TierCheckboxes({
  idPrefix,
  tiers,
  selected,
  onChange,
  disabled,
  invalidMessageId,
}: {
  idPrefix: string;
  tiers: Pick<CampaignPlanTier, "id" | "name">[];
  selected: string[];
  onChange: (tierIds: string[]) => void;
  disabled?: boolean;
  /** The id of an error message describing these boxes, when invalid. */
  invalidMessageId?: string;
}) {
  return (
    <div
      role="group"
      aria-label="Tiers"
      aria-invalid={invalidMessageId ? true : undefined}
      aria-describedby={invalidMessageId}
      className="flex flex-wrap gap-4"
    >
      {tiers.map((tier) => (
        <div key={tier.id} className="flex items-center gap-2">
          <Checkbox
            id={`${idPrefix}-${tier.id}`}
            checked={selected.includes(tier.id)}
            disabled={disabled}
            onCheckedChange={(checked) =>
              onChange(
                checked
                  ? [...selected, tier.id]
                  : selected.filter((id) => id !== tier.id),
              )
            }
          />
          <Label htmlFor={`${idPrefix}-${tier.id}`} className="font-normal">
            {tier.name}
          </Label>
        </div>
      ))}
    </div>
  );
}

/**
 * "Goes to tiers" for one mailing, call round or email tranche that goes to
 * tiers (not a follow-up). While it is a Draft the tiers can be changed;
 * after that the recipients are fixed, so it just says who it goes to. A
 * mailing or call round never defaults to everyone; an email tranche with
 * no tiers goes to the whole audience. A mailing's chosen tiers can each
 * carry an optional artwork code (the studio's note of which design that
 * tier's people get), written into the mailing file. The codes stay
 * editable while the mailing is Ready, until it is marked posted.
 */
export function StepTiersEditor({
  campaignId,
  step,
  tiers,
  onChanged,
}: {
  campaignId: string;
  step: CampaignPlanStep;
  tiers: CampaignPlanTier[];
  onChanged: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const [selected, setSelected] = useState<string[]>(step.target_tier_ids);
  const [codes, setCodes] = useState<Record<string, string>>(
    step.artwork_codes,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeErrors, setCodeErrors] = useState<Record<string, string>>({});
  const takesArtworkCodes = step.channel === "direct_mail";
  const chosenCodes = Object.fromEntries(
    selected
      .map((id) => [id, (codes[id] ?? "").trim()] as const)
      .filter(([, code]) => code !== ""),
  );
  const isDirty =
    selected.length !== step.target_tier_ids.length ||
    selected.some((id) => !step.target_tier_ids.includes(id)) ||
    codesChanged(chosenCodes, step.artwork_codes, selected);

  if (step.state === "ready" && takesArtworkCodes) {
    return (
      <ReadyMailingArtworkCodes
        campaignId={campaignId}
        step={step}
        tiers={tiers}
        onChanged={onChanged}
      />
    );
  }

  if (step.state !== "draft") {
    const codesLine = artworkCodesLine(step, tiers);
    return (
      <div className="space-y-1 text-sm text-foreground">
        <p>Goes to: {stepAudienceLabel(step, tiers)}</p>
        {codesLine && <p className="text-muted-foreground">{codesLine}</p>}
      </div>
    );
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setCodeErrors({});
    try {
      await dataAdapter.setCampaignStepTiers(
        campaignId,
        step.id,
        selected,
        takesArtworkCodes ? chosenCodes : {},
      );
      onChanged();
    } catch (e) {
      console.error(
        "marketing.campaign.step_tiers.save_failed",
        failureLogFields(e, { campaign_id: campaignId, step_id: step.id }),
      );
      const refusal = readCampaignActionError(
        e,
        "Couldn't save the tiers. Please try again.",
      );
      setCodeErrors(artworkCodeErrors(refusal.fields));
      setError(refusal.summary);
    } finally {
      setSaving(false);
    }
  }

  const errorId = `step-tiers-${step.id}-error`;
  return (
    <div className="rounded-lg border border-border p-4 space-y-2">
      <h3 className="font-medium">Goes to tiers</h3>
      {tiers.length === 0 ? (
        <p className="text-sm text-muted-foreground">{NO_TIERS_YET_NOTE}</p>
      ) : (
        <>
          <TierCheckboxes
            idPrefix={`step-tiers-${step.id}`}
            tiers={tiers}
            selected={selected}
            onChange={setSelected}
            disabled={saving}
            invalidMessageId={error ? errorId : undefined}
          />
          <p className="text-xs text-muted-foreground">
            {tierChoiceHelp(step.channel)}
          </p>
          {takesArtworkCodes && selected.length > 0 && (
            <ArtworkCodeInputs
              stepId={step.id}
              tiers={tiers.filter((tier) => selected.includes(tier.id))}
              codes={codes}
              onChange={setCodes}
              errors={codeErrors}
              disabled={saving}
            />
          )}
          {isDirty && (
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save tiers"}
            </Button>
          )}
        </>
      )}
      {error && (
        <p
          id={errorId}
          className="text-sm text-red-700 dark:text-red-300"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
}

/** Whether the trimmed codes for ``tierIds`` differ from the saved ones. */
function codesChanged(
  chosen: Record<string, string>,
  saved: Record<string, string>,
  tierIds: string[],
): boolean {
  const before = Object.fromEntries(
    tierIds.filter((id) => saved[id]).map((id) => [id, saved[id]]),
  );
  return JSON.stringify(chosen) !== JSON.stringify(before);
}

/** The field errors for artwork codes, keyed by tier id. */
function artworkCodeErrors(
  fields: Record<string, string>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(fields)
      .filter(([field]) => field.startsWith("artwork_codes."))
      .map(([field, message]) => [
        field.slice("artwork_codes.".length),
        message,
      ]),
  );
}

/** One optional artwork-code input per tier, each with its own error. */
function ArtworkCodeInputs({
  stepId,
  tiers,
  codes,
  onChange,
  errors,
  disabled,
}: {
  stepId: string;
  tiers: Pick<CampaignPlanTier, "id" | "name">[];
  codes: Record<string, string>;
  onChange: (codes: Record<string, string>) => void;
  errors: Record<string, string>;
  disabled: boolean;
}) {
  return (
    <div className="space-y-2 pt-2">
      <p className="text-sm font-medium">Artwork code (optional)</p>
      <p className="text-xs text-muted-foreground">
        Which design or file each tier's people get. It goes into the mailing
        file beside each person.
      </p>
      {tiers.map((tier) => {
        const inputId = `artwork-code-${stepId}-${tier.id}`;
        const errorId = `${inputId}-error`;
        return (
          <div key={tier.id} className="flex flex-wrap items-center gap-3">
            <Label htmlFor={inputId} className="w-32 font-normal">
              {tier.name}
            </Label>
            <Input
              id={inputId}
              value={codes[tier.id] ?? ""}
              maxLength={MAX_ARTWORK_CODE_LENGTH}
              placeholder="e.g. TIME-VIP"
              aria-label={`Artwork code for ${tier.name}`}
              disabled={disabled}
              onChange={(e) =>
                onChange({ ...codes, [tier.id]: e.target.value })
              }
              aria-invalid={errors[tier.id] ? true : undefined}
              aria-describedby={errors[tier.id] ? errorId : undefined}
              className="w-56"
            />
            {errors[tier.id] && (
              <p
                id={errorId}
                className="text-sm text-red-600 dark:text-red-400"
              >
                {errors[tier.id]}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A Ready mailing: its tiers are fixed with its recipients, but its
 * artwork codes can still be changed until it is marked posted (the next
 * mailing file download carries them).
 */
function ReadyMailingArtworkCodes({
  campaignId,
  step,
  tiers,
  onChanged,
}: {
  campaignId: string;
  step: CampaignPlanStep;
  tiers: CampaignPlanTier[];
  onChanged: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const [codes, setCodes] = useState<Record<string, string>>(
    step.artwork_codes,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeErrors, setCodeErrors] = useState<Record<string, string>>({});
  const chosen = Object.fromEntries(
    step.target_tier_ids
      .map((id) => [id, (codes[id] ?? "").trim()] as const)
      .filter(([, code]) => code !== ""),
  );
  const isDirty = codesChanged(
    chosen,
    step.artwork_codes,
    step.target_tier_ids,
  );

  async function handleSave() {
    setSaving(true);
    setError(null);
    setCodeErrors({});
    try {
      await dataAdapter.setCampaignStepArtworkCodes(
        campaignId,
        step.id,
        chosen,
      );
      onChanged();
    } catch (e) {
      console.error(
        "marketing.campaign.artwork_codes.save_failed",
        failureLogFields(e, { campaign_id: campaignId, step_id: step.id }),
      );
      const refusal = readCampaignActionError(
        e,
        "Couldn't save the artwork codes. Please try again.",
      );
      setCodeErrors(artworkCodeErrors(refusal.fields));
      setError(refusal.summary);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-lg border border-border p-4 space-y-2">
      <p className="text-sm text-foreground">
        Goes to: {stepAudienceLabel(step, tiers)}
      </p>
      {step.target_tier_ids.length > 0 && (
        <ArtworkCodeInputs
          stepId={step.id}
          tiers={tiers.filter((tier) => step.target_tier_ids.includes(tier.id))}
          codes={codes}
          onChange={setCodes}
          errors={codeErrors}
          disabled={saving}
        />
      )}
      {isDirty && (
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save artwork codes"}
        </Button>
      )}
      {error && (
        <p className="text-sm text-red-700 dark:text-red-300" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
